import { useEffect, useRef } from 'react'
import { TURNSTILE_SITE_KEY } from '@/api'

/**
 * Widget do Cloudflare Turnstile (anti-bot) usado no login e no cadastro.
 *
 * Sem `VITE_TURNSTILE_SITE_KEY` configurada, renderiza um espaço reservado — o
 * layout não quebra e o formulário continua utilizável em desenvolvimento.
 *
 * O token devolvido em `onVerify` vai no corpo da requisição; a validação
 * server-side (`/siteverify`) é responsabilidade do backend.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        },
      ) => string
      remove: (widgetId: string) => void
    }
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile')))
      return
    }

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile'))
    document.head.appendChild(script)
  })

  return scriptPromise
}

interface TurnstileProps {
  onVerify: (token: string) => void
  onExpire?: () => void
}

export function Turnstile({ onVerify, onExpire }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  // As callbacks entram em ref para não recriar o widget a cada render.
  const onVerifyRef = useRef(onVerify)
  const onExpireRef = useRef(onExpire)
  onVerifyRef.current = onVerify
  onExpireRef.current = onExpire

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return

    let cancelled = false

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          callback: (token) => onVerifyRef.current(token),
          'expired-callback': () => onExpireRef.current?.(),
        })
      })
      .catch(() => {
        // Falha no carregamento não bloqueia o formulário; o backend ainda
        // rejeita a requisição sem token válido.
      })

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
      }
    }
  }, [])

  if (!TURNSTILE_SITE_KEY) {
    return (
      <div className="flex h-[65px] items-center justify-center rounded border border-dashed border-line-strong bg-surface-muted px-4 text-center text-xs text-ink-faint">
        Verificação anti-bot — configure VITE_TURNSTILE_SITE_KEY
      </div>
    )
  }

  return <div ref={containerRef} className="min-h-[65px]" />
}
